import {flatten, unflatten} from 'flat';
import _ from 'lodash';
import {
    CatalogProductStatus,
    Product,
    ProductAttributesField,
    ProductData,
    ProductDetailsField,
    ProductField,
    ProductInCatalog,
    ProductType,
} from '@treez/commons/sharedTypings/product';
import {requiredAndInvalidProductFieldsByType} from '@treez/commons/utils/product';
import {validateProduct} from '@treez/commons/jsonSchema/products/product';
import {ProductRegion} from '@treez/commons/jsonSchema/products/regionSchemas';
import {
    SearchCriteria,
    searchProductAPI,
    SearchFilter,
    ProductsPresenceAcrossStores,
    exportProductAPI,
} from '~/utils/ProductAPI';
import {StoreAttributes} from './directory';
import {formatNumberToPrecision} from './format';
import {ProductFilters, ProductParameters, SortDirection} from './sharedInterfaces';

const {BRAND_NAME, CLASSIFICATION, STATUS, SUBTYPE, TYPE} = ProductField;

const {FLAVOR, EFFECT, INGREDIENT, INTERNAL, GENERAL} = ProductAttributesField;

export interface ProductNavigationAttributes {
    filters: SearchFilter[];
    page: number;
    pageSize: number;
    productView: ProductListView;
    searchTerm: string;
    sortCategory: string;
    sortOrder: SortDirection;
    status?: CatalogProductStatus[];
}

export enum ProductListView {
    CARD = 'card',
    MERGE = 'merge',
    STORE_MATRIX = 'storeMatrixView',
    TABLE = 'table',
    TILE = 'tile',
}

export interface SearchResultsAttributes {
    data: ProductInCatalog[];
    total: number;
}

export const EACH_PRODUCT_TYPES = [ProductType.MERCH, ProductType.PILL, ProductType.PLANT];

export const buildProductFiltersFromParams = (productParameters: ProductParameters) => {
    const productFilters: SearchFilter[] = [];

    const isNotEmptyOrUndefined = (valueToInspect: any) => {
        return !(_.isEmpty(valueToInspect) || valueToInspect == null);
    };

    _.forEach(productParameters, (values, key) => {
        if (isNotEmptyOrUndefined(values)) {
            const fieldFilter = {
                values,
                fuzzy: false,
            } as SearchFilter;

            switch (key) {
                case 'brand':
                    fieldFilter.field = BRAND_NAME;
                    break;
                case 'classification':
                    fieldFilter.field = CLASSIFICATION;
                    break;
                case 'effect':
                    fieldFilter.field = `attribute.${EFFECT}`;
                    break;
                case 'flavor':
                    fieldFilter.field = `attribute.${FLAVOR}`;
                    break;
                case 'generalAttribute':
                    fieldFilter.field = `attribute.${GENERAL}`;
                    break;
                case 'ingredient':
                    fieldFilter.field = `attribute.${INGREDIENT}`;
                    break;
                case 'internalTag':
                    fieldFilter.field = `attribute.${INTERNAL}`;
                    break;
                case 'status':
                    fieldFilter.field = STATUS;
                    break;
                case 'subtype':
                    fieldFilter.field = SUBTYPE;
                    break;
                case 'type':
                    fieldFilter.field = TYPE;
                    break;
            }

            if (fieldFilter.field) {
                productFilters.push(fieldFilter);
            }
        }
    });

    return productFilters;
};

export const cleanProductToEdit = (
    productBeingEdited: Partial<ProductInCatalog | Product>
): Partial<ProductInCatalog | Product> => {
    const {amount} = productBeingEdited;

    const cleanedProduct = {
        ...productBeingEdited,
        amount: amount ? formatNumberToPrecision(amount, 2) : null,
    };

    if (productBeingEdited.type) {
        // Today sellTreez is inputting values of 0 in fields that are not relevant to product types. We need these
        // to be cleaned off the product as the field isn't populated to edit in the product form (because its not a valid field for that type)
        const productValidationInformation = requiredAndInvalidProductFieldsByType(
            productBeingEdited.type,
            productBeingEdited.subtype
        );
        const invalidFields = _.get(productValidationInformation, 'invalid');

        const invalidFieldPaths = _.map(invalidFields, (invalidField) => {
            return getPathToField(invalidField);
        });

        return _.omit(cleanedProduct, invalidFieldPaths) as Partial<ProductInCatalog>;
    }
    return cleanedProduct as Partial<ProductInCatalog>;
};

const cleanSearchObject = (
    searchParameters: Partial<ProductNavigationAttributes>,
    catalogId?: number
) => {
    const {
        page = 1,
        pageSize,
        searchTerm,
        sortCategory,
        sortOrder = SortDirection.ASCENDING,
        status = [CatalogProductStatus.ACTIVE],
        filters = [],
    } = searchParameters;

    const pageCleaned = _.isNumber(page) ? page : _.toNumber(page);
    const pageSizeCleaned = _.isNumber(pageSize) ? pageSize : _.toNumber(pageSize);

    if (_.isNaN(pageCleaned)) {
        throw new Error(
            `page number passed to search product api is invalid: ${searchParameters.page}`
        );
    }

    if (_.isNaN(pageSizeCleaned)) {
        throw new Error(
            `page size passed to search product api is invalid: ${searchParameters.pageSize}`
        );
    }

    const productStatus = status;

    const searchObject: SearchCriteria = {
        catalogId,
        orderByRank: true,
        page: pageCleaned,
        pageSize: pageSizeCleaned,
        searchString: _.isEmpty(searchTerm) ? undefined : searchTerm,
        filters: _.isEmpty(filters) ? undefined : filters,
        status: productStatus,
    };

    searchObject.sortBy = {
        order: sortOrder,
        field: 'type',
    };

    if (sortCategory) {
        switch (sortCategory) {
            case 'displayname':
                searchObject.sortBy.field = 'name';
                break;
            case 'brandname':
                searchObject.sortBy.field = 'brandName';
                break;
            default:
                searchObject.sortBy.field = sortCategory;
                break;
        }

        searchObject.orderByRank = false;
    }

    const cleanedSearchObject = _.omitBy(searchObject, _.isUndefined);

    return cleanedSearchObject;
};

export const exportProducts = async (
    searchParameters: Partial<ProductNavigationAttributes>,
    catalogId?: number
): Promise<string> => {
    const cleanedSearchObject = cleanSearchObject(searchParameters, catalogId);

    const data = await exportProductAPI(cleanedSearchObject);

    return data;
};

export const flattenProduct = (product: Product | Partial<Product>) => {
    return {
        ...product,
        ...product.attributes,
        ...product.details,
    };
};

export const getThcCbdTotals = (cbdMg, thcMg) => {
    if (thcMg && cbdMg) {
        return `${thcMg}mg THC / ${cbdMg}mg CBD`;
    }
    if (thcMg && cbdMg == null) {
        return `${thcMg}mg THC`;
    }
    if (thcMg == null && cbdMg) {
        return `${cbdMg}mg CBD`;
    }
    return '-';
};

export const getOverriddenValue = (
    product: Partial<ProductInCatalog>,
    linkedBrandProduct: ProductInCatalog | undefined,
    fieldName: ProductData
) => {
    const {baseProduct, catalogOverrides, productOverrides} = product;

    const path = getPathToField(fieldName);
    const isLinked = Boolean(linkedBrandProduct);
    const hasCatalogOverrides = _.has(catalogOverrides, path);
    const hasProductOverrides = _.has(productOverrides, path);

    if (hasCatalogOverrides && hasProductOverrides) {
        return _.get(productOverrides, path);
    }
    if (isLinked) {
        return _.get(linkedBrandProduct, path);
    }
    return _.get(baseProduct, path);
};

export const getPathToField = (fieldName: ProductData) => {
    let path: string;
    if (fieldName === ProductField.DESCRIPTIONS) {
        path = `descriptions.main`;
    } else if (_.includes(ProductAttributesField, fieldName)) {
        path = `attributes.${fieldName}`;
    } else if (_.includes(ProductDetailsField, fieldName)) {
        path = `details.${fieldName}`;
    } else {
        path = fieldName;
    }

    return path;
};

export const getProductDiff = (
    productBefore: Partial<ProductInCatalog>,
    productAfter: Partial<ProductInCatalog>
): Partial<ProductInCatalog> => {
    const flattenBefore = flatten(productBefore, {safe: true});
    const flattenAfter = flatten(productAfter, {safe: true});

    const overrideObject: Partial<ProductInCatalog> = {};

    _.keys(flattenAfter)
        .filter((key) => flattenBefore[key] !== flattenAfter[key])
        .forEach((key) => {
            overrideObject[key] = flattenAfter[key];
        });

    return unflatten(overrideObject);
};

export const mapStoreNameToCatalogPresence = (
    productPresence: ProductsPresenceAcrossStores,
    stores: StoreAttributes[]
): ProductsPresenceAcrossStores => {
    return _.mapValues(productPresence, (storePresence) => {
        return _.map(storePresence, (presence) => {
            const store = _.find(stores, (store) => {
                return store.catalogId === presence.id;
            }) as StoreAttributes;

            const storeName = store.name;

            return {
                ...presence,
                storeName,
            };
        });
    }) as ProductsPresenceAcrossStores;
};

export const sortAttributesAlphabetically = (attributesToSort: string[] | undefined) => {
    if (attributesToSort == null) {
        return [];
    }

    const listFiltered = _.filter(attributesToSort, (attribute) => {
        return attribute != null && attribute !== '';
    });

    return _.orderBy(listFiltered, (attribute) => attribute.toLowerCase(), ['asc']);
};

export const updateProductState = (
    field: ProductData,
    value: any,
    existingProduct: Partial<ProductInCatalog> | ProductInCatalog
) => {
    const noValueGiven = value == null || value == '';

    if (field === ProductField.DESCRIPTIONS) {
        return {
            ...existingProduct,
            descriptions: {
                main: value,
            },
        };
    }
    if (_.includes(ProductDetailsField, field)) {
        const existingDetails = _.get(existingProduct, 'details', {});

        if (noValueGiven) {
            return {
                ...existingProduct,
                details: {
                    ..._.omit(existingDetails, [field]),
                },
            };
        }
        return {
            ...existingProduct,
            details: {
                ...existingDetails,
                [field]: value,
            },
        };
    }
    if (_.includes(ProductAttributesField, field)) {
        const existingAttributes = _.get(existingProduct, 'attributes', {});

        if (noValueGiven) {
            return {
                ...existingProduct,
                attributes: {
                    ..._.omit(existingAttributes, [field]),
                },
            };
        }
        return {
            ...existingProduct,
            attributes: {
                ...existingAttributes,
                [field]: value,
            },
        };
    }
    if (noValueGiven) {
        return {
            ..._.omit(existingProduct, [field]),
        };
    }
    return {
        ...existingProduct,
        [field]: value,
    };
};

/**
 * Updates entire producte state. Only intended to be used for Merge Redux State (MergeReducer)
 * @param sourceProduct product from which new values will be obtained.
 * @param existingProduct the existing product.
 * @returns a new updated product
 */
export const updateEntireProductState = (
    sourceProduct: Partial<ProductInCatalog>,
    existingProduct: Partial<ProductInCatalog>
) => {
    let newUpdatedProduct = {...existingProduct};

    const excludedFields = ['id'];

    for (const field in sourceProduct) {
        if (field === ProductField.DESCRIPTIONS && sourceProduct[field] != null) {
            const main = _.get(sourceProduct[field], 'main');

            newUpdatedProduct = updateProductState(field as ProductData, main, newUpdatedProduct);
        } else if (!excludedFields.includes(field)) {
            newUpdatedProduct = updateProductState(
                field as ProductData,
                sourceProduct[field],
                newUpdatedProduct
            );
        }
    }

    return newUpdatedProduct;
};

export const renderValueForUI = (value: string | undefined | number): string => {
    if (value == null) {
        return '';
    }
    if (_.isNumber(value)) {
        return String(value);
    }
    return _.startCase(value);
};

export const renderListForUI = (list: string[]): string => {
    if (list.length === 0) {
        return '';
    }
    return _.join(
        _.map(list, (value) => {
            return _.startCase(value);
        }),
        ', '
    );
};

export const returnNewAttributes = (
    newlyAddedAttributes: string[],
    existingAttributes: string[]
): string[] => {
    return _.filter(newlyAddedAttributes, (attributeOnSubmittedProduct) => {
        return !_.includes(existingAttributes, attributeOnSubmittedProduct);
    });
};

export const searchProducts = async (
    searchParameters: Partial<ProductNavigationAttributes>,
    catalogId?: number
): Promise<SearchResultsAttributes> => {
    const cleanedSearchObject = cleanSearchObject(searchParameters, catalogId);
    const data = await searchProductAPI(cleanedSearchObject);

    return data;
};

export const determineIfAllProductsSelected = (
    products: Partial<ProductInCatalog>[],
    selectedProductIds: number[]
): boolean => {
    return (
        _.reduce(
            products,
            (acc: number, currentValue: Partial<ProductInCatalog>) => {
                return acc * (_.includes(selectedProductIds, currentValue.id) ? 1 : 0);
            },
            1
        ) === 1
    );
};

export const translateProductParamsToProductFilters = (
    productParams: ProductParameters
): ProductFilters => {
    const {
        brand,
        classification,
        effect,
        flavor,
        generalAttribute,
        ingredient,
        internalTag,
        subtype,
        type,
    } = productParams;

    return _.omitBy(
        {
            brands: brand,
            subtypes: subtype,
            classifications: classification,
            internalTags: internalTag,
            flavors: flavor,
            effects: effect,
            ingredients: ingredient,
            generalAttributes: generalAttribute,
            types: type,
        },
        (values) => {
            return _.isEmpty(values) || values == null;
        }
    );
};

export const translateProductFiltersToParams = (productFilters: ProductFilters) => {
    const {
        brands,
        subtypes,
        classifications,
        internalTags,
        flavors,
        effects,
        ingredients,
        generalAttributes,
        types,
        statuses,
    } = productFilters;

    return _.omitBy(
        {
            brand: brands,
            subtype: subtypes,
            classification: classifications,
            internalTag: internalTags,
            flavor: flavors,
            effect: effects,
            ingredient: ingredients,
            generalAttribute: generalAttributes,
            type: types,
            status: statuses,
        },
        (values) => {
            return _.isEmpty(values) || values == null;
        }
    );
};

/**
 * Wrapping validateProduct to provide a more user friendly error message.
 */
export const validateProductWrapper = (
    product: Partial<Product>,
    region: ProductRegion | undefined
) => {
    const wrappedProduct = {
        ...product,
        details: product.details || {},
    };

    return validateProduct(wrappedProduct, region);
};
